import axios from "axios";
import { ADMIN_KEY, APP_URL, URL as CLIENT_URL } from "../../config/config";

// whatsapp-rest-api superadmin compliance routes (AGN-206). GETs need
// spam-review read, POSTs spam-review write.
const COMPLIANCE = `${CLIENT_URL}/superadmin/compliance`;

export const PAGE_SIZE = 20;

// severity (pending tab only): flagged = open block items, review = open review (no block),
// uncertain = only "report" items. Older backends: flagged = block + review, no review.
// label / policy: comma lists (any match); from / to: ISO bounds on the item's createdAt.
export const fetchCases = ({ tab, subjectType, severity, label, policy, from, to, assistantId, clientId, page = 1, limit = PAGE_SIZE }) => {
  const params = new URLSearchParams();
  params.set("tab", tab);
  if (severity) params.set("severity", severity);
  if (subjectType) params.set("subjectType", subjectType);
  if (label) params.set("label", label);
  if (policy) params.set("policy", policy);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (assistantId) params.set("assistantId", assistantId);
  if (clientId) params.set("clientId", clientId);
  params.set("page", page);
  params.set("limit", limit);
  return axios.get(`${COMPLIANCE}/cases?${params.toString()}`).then((r) => r.data || {});
};

// Inbox header counts: { pending, highRisk, held, suspended, flagged?, uncertain?,
// facets?: { labels: { label: n }, policies: { id: n } } } (facets count open items).
export const fetchCaseStats = () => axios.get(`${COMPLIANCE}/case-stats`).then((r) => r.data || {});

export const fetchCase = (assistantId) =>
  axios
    .get(`${COMPLIANCE}/case?assistantId=${encodeURIComponent(assistantId)}`)
    .then((r) => r.data || null);

const withNote = (body, note) => (note ? { ...body, note } : body);
// label: the correct content label per the reviewer (benign,
// ordinary_commercial_promotion, spam, phishing, ...). Omitted = backend default.
const withLabel = (body, label) => (label ? { ...body, label } : body);

// action: dismiss | suspend_project | suspend_account — resolves every open item.
// For dismiss the label (benign | ordinary_commercial_promotion) applies to every item.
// restoreRejected (dismiss only): also re-submit to Meta every template that was held
// and rejected here earlier; held counts gain restored / restoreMetaRejected /
// restoreGone / restoreFailed. Older backends ignore it.
// businessContext ({ about, useCases }): saved on the owner for future screening.
// Required (400) unless the owner already has saved context; sending it updates it.
const withContext = (body, businessContext) => (businessContext ? { ...body, businessContext } : body);

// disableAgents (suspend actions, always sent): also disable all agents of the account
// (the owner and every team member). Reinstate undoes it.
// allProjects (dismiss only, sent only when true): dismiss every project of the owner the
// same way; the response gains { allProjects, projects } with summed counts.
export const decideCase = (assistantId, action, note, label, restoreRejected, businessContext, disableAgents, allProjects) =>
  axios
    .post(
      `${COMPLIANCE}/case/decision`,
      withContext(
        withLabel(
          withNote(
            {
              assistantId,
              action,
              ...(action === "dismiss" ? { restoreRejected: !!restoreRejected } : {}),
              ...(action === "dismiss" && allProjects ? { allProjects: true } : {}),
              ...(action.startsWith("suspend") ? { disableAgents: !!disableAgents } : {}),
            },
            note
          ),
          label
        ),
        businessContext
      )
    )
    .then((r) => r.data);

// action: ok | delete (delete removes the template at Meta; 502 if Meta fails).
// businessContext: sent with ok (same rule as decideCase).
export const resolveItem = (id, action, note, label, businessContext) =>
  axios
    .post(
      `${COMPLIANCE}/screenings/${encodeURIComponent(id)}/resolve`,
      withContext(withLabel(withNote({ action }, note), label), businessContext)
    )
    .then((r) => r.data);

export const suspendProject = (assistantId, note) =>
  axios.post(`${COMPLIANCE}/suspend`, withNote({ assistantId, scope: "project" }, note)).then((r) => r.data);

// Owner-level settings; each applies to every project of the project's owner.
// Older backends: 404.
// senderType: aggregator | service_provider | null (unmark); industry: INDUSTRIES value.
// Resolves to { senderType, industry, projects: <n updated> }.
export const setOwnerSettings = (assistantId, senderType, industry, note) =>
  axios
    .post(`${COMPLIANCE}/owner-settings`, withNote({ assistantId, senderType, ...(senderType && industry ? { industry } : {}) }, note))
    .then((r) => r.data);

// Whitelisted owners skip spam screening (known scam hosts are still blocked).
// Resolves to { whitelisted, projects: <n updated> }.
export const setWhitelist = (assistantId, whitelisted, note) =>
  axios.post(`${COMPLIANCE}/whitelist`, withNote({ assistantId, whitelisted: !!whitelisted }, note)).then((r) => r.data);

// The reinstate route takes no note.
export const reinstate = (assistantId, scope) =>
  axios.post(`${COMPLIANCE}/reinstate`, { assistantId, scope }).then((r) => r.data);

// Owner login, as on the Assistants page (generate-any-token is gated on the
// assistants "login" action). Resolves to the dashboard login URL.
export const ownerLoginUrl = async (clientId, assistantId, whiteLabelUrl) => {
  const {
    data: { token },
  } = await axios.post(
    `${CLIENT_URL}/superadmin/generate-any-token`,
    { _id: clientId, assistantId, type: "agent" },
    { headers: { "x-aisensy-admin-token-key": ADMIN_KEY } }
  );
  if (!token) throw new Error("No login token returned");
  return whiteLabelUrl ? `${whiteLabelUrl}/login?token=${token}` : `${APP_URL}/login?token=${token}&firstProjectId=${assistantId}`;
};

// White-label dashboard base URL for a partner (same lookup as the Assistants page).
export const fetchWhiteLabelUrl = (partnerId) =>
  axios
    .get(`${CLIENT_URL}/partner-apis/v1/partner/get-partner-details?partnerId=${encodeURIComponent(partnerId)}`)
    .then((r) => {
      if (!r.data?.whiteLabelUrl) throw new Error("This partner has no white-label URL");
      return r.data.whiteLabelUrl;
    });
